import type { Category } from '@cobrastyle/shared-types';

/**
 * Which category is "the brands page", as a bare Magento `url_key`.
 *
 * Hardcoded on purpose: there is one store today, so a config indirection would
 * buy nothing. Deliberately a single constant rather than a list: the header
 * fetches exactly one brands category, so only a list's first element could ever
 * have been used, and reaching for `[0]` from the header made this two places to
 * edit instead of one. When a second store needs a different key (e.g. an English
 * "brands"), widen it here and teach `isBrandsCategory` and the header's fetch
 * about the list together.
 */
export const BRANDS_URL_KEY = 'varumarken';

/**
 * A category child that may carry Torsbo's own `is_main_brand` attribute.
 *
 * The flag is merged onto children by the `category` override in
 * `storefront.config.ts`; it is absent from the shared `Category` type because it
 * is a client-specific Magento attribute. Optional on purpose — a child that
 * never went through the override (a nested subcategory, a non-brands page) has
 * no flag, and `mainBrands()` must treat that as "not a main brand" rather than
 * throwing.
 *
 * Declared here rather than in `brand-extras.ts` so that client components can
 * import brand types without pulling the Magento client into the browser bundle.
 */
export type BrandChild = Category & { isMainBrand?: boolean };

/**
 * `'varumarken/sauer.html'` -> `'sauer'`.
 *
 * `Category.urlKey` (and `NavigationItem.urlKey`) is not Magento's bare
 * `url_key`: the adapter's `buildCategoryUrl()` returns the full ancestor path
 * with the store's `category_url_suffix` applied — `.html` on this store. So the
 * brands category arrives as `varumarken.html` and its children as
 * `varumarken/sauer.html`.
 *
 * Both the brands gate and the main-brand lookup compare against bare keys, so
 * they normalise through this first. The last path segment is what identifies a
 * category — the same rule the adapter's own `getByUrlKey` fallback and
 * `navigation.tsx`'s `pathSegments()` use.
 *
 * This is the one place in the codebase that knows about the suffix: links keep
 * using `urlKey` verbatim, because the suffixed path is the correct URL.
 *
 * The suffix regex is safe for this catalogue — none of the 73 brand `url_key`s
 * contains a dot.
 */
function brandKey(urlKey: string): string {
  return (urlKey.split('/').pop() ?? '').replace(/\.[a-z0-9]{1,5}$/i, '');
}

export function isBrandsCategory(urlKey: string): boolean {
  return brandKey(urlKey) === BRANDS_URL_KEY;
}

/**
 * An individual brand's page — a direct child of the brands category, e.g.
 * `varumarken/swarovski-optik.html`.
 *
 * Direct children only. `varumarken.html` is the index, not a brand, and
 * `varumarken/swarovski/z8i.html` is a range within a brand, so neither qualifies.
 */
export function isBrandPage(urlKey: string): boolean {
  const segments = urlKey.split('/').filter(Boolean);
  if (segments.length !== 2) return false;
  return brandKey(segments[0]) === BRANDS_URL_KEY;
}

/**
 * Magento serves an unset category image as its Luma placeholder rather than
 * nothing, so a non-null `image` carries no information. Of 73 brands, 53 return
 * this path and 1 has a real logo — rendering the placeholder would put the same
 * grey stock photo on 53 cards.
 *
 * Matched by path segment, not exact URL: the `/static/versionNNNN/` prefix
 * changes on every Magento deploy.
 */
const PLACEHOLDER_SEGMENT = '/images/category/placeholder/';

export function brandImage(image?: string): string | undefined {
  if (!image) return undefined;
  return image.includes(PLACEHOLDER_SEGMENT) ? undefined : image;
}

/**
 * The children Magento flags as main brands, in the order Magento returned them
 * (its own `position` order) — merchandising owns both the selection and the
 * order, so there is nothing to curate here.
 *
 * The flag arrives via the `category` override in `storefront.config.ts`. A child
 * with no flag at all — not decorated, or the extras fetch degraded — is simply
 * not a main brand, so the row renders empty rather than failing. Callers already
 * treat an empty result as "render nothing".
 */
export function mainBrands(children: BrandChild[]): BrandChild[] {
  return children.filter((child) => child.isMainBrand === true);
}

export interface BrandLetterGroup {
  letter: string;
  brands: Category[];
}

/**
 * Brands grouped by leading letter under Swedish collation, letters with no
 * brands omitted. A name starting with a digit or symbol gets its own group,
 * which the index renders like any other — there is no fixed alphabet to miss
 * it now that the jump bar is gone.
 */
export function groupByLetter(children: Category[]): BrandLetterGroup[] {
  const sorted = [...children].sort((a, b) => a.name.localeCompare(b.name, 'sv'));
  const groups = new Map<string, Category[]>();

  for (const brand of sorted) {
    const letter = (brand.name.trim()[0] ?? '#').toUpperCase();
    const existing = groups.get(letter);
    if (existing) existing.push(brand);
    else groups.set(letter, [brand]);
  }

  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b, 'sv'))
    .map(([letter, brands]) => ({ letter, brands }));
}
